import { EXPORT_FPS as VIDEO_FPS } from '@/lib/timeline/exportFrameTiming';
 
 

import lodash from 'lodash'

/**
 * Converts an array of words in Whisper format into an array of lines.
 *
 * @param {Array} words - Array of words in Whisper format. Each word is an
 *                        object with `start`, `end`, and `text` properties.
 * @param {Object} options - Options for splitting lines.
 * @param {number} options.maxSeconds - Maximum duration (in seconds) for each line.
 * @param {number} options.maxWords - Maximum number of words in each line.
 * @param {number} options.maxCharacters - Maximum number of characters in each line.
 * @returns {Array} - An array of lines, each being an object with `text`,
 *                    `start`, and `end` properties.
 */
function convertWordsToLines(words, { maxSeconds = 20, maxWords = 20, maxCharacters = 50 } = {}) {
  const lines = []
  let currentLine = []
  let currentStart = null

  words.forEach((word) => {
    const wordText = word.punctuated_word.trim()

    // Initialize start time for the current line
    if (currentStart === null) {
      currentStart = word.start
    }

    // Calculate current line properties
    const currentText = currentLine.map((w) => w.text).join(' ')
    const currentDuration = word.end - currentStart
    const currentWordCount = currentLine.length
    const currentCharCount = currentText.length + wordText.length + 1 // +1 for the space

    // Check if adding this word exceeds any limits
    const exceedsLimits = currentDuration > maxSeconds
      || currentWordCount >= maxWords
      || currentCharCount > maxCharacters

    if (exceedsLimits && currentLine.length > 0) {
      // Finalize the current line
      lines.push({
        text: currentLine.map((w) => w.punctuated_word).join(' '),
        start: currentStart,
        end: currentLine[currentLine.length - 1].end,
        words: currentLine,
      })

      // Start a new line
      currentLine = [word]
      currentStart = word.start
    } else {
      // Add the word to the current line
      currentLine.push(word)
    }
  })

  // Add the last line if any
  if (currentLine.length > 0) {
    lines.push({
      text: currentLine.map((w) => w.punctuated_word).join(' '),
      start: currentStart,
      end: currentLine[currentLine.length - 1].end,
      words: currentLine,
    })
  }

  return lines
}

const convertTimeStringToSeconds = (time) => {
  const [hh, mm, ss, ms = 0] = time.split(/[.,:]/).map(Number)
  return (ms + ss * 1000 + mm * 60 * 1000 + hh * 60 * 60 * 1000) / 1000
}



const reduceRedundantCuts = (cuts) => {
  const reducedCuts = []
  let currentCut = null
  for (const cut of cuts) {
    if (currentCut?.end >= cut.start) {
      currentCut.end = cut.end
    } else {
      currentCut = null
    }

    if (!currentCut) {
      currentCut = {
        start: cut.start,
        end: cut.end,
      }
      reducedCuts.push(currentCut)
    }
  }

  return reducedCuts
}

const getSilenceClipIntervals = (videoMetadata, {
  segments,
  cut,
  minCut,
}) => {
  if (!cut) {
    return [{ start: 0, end: videoMetadata.durationInSeconds }]
  }

  const cuts = []
  for (const segment of segments) {
    for (const word of segment.words) {
      if (!word.word.trim()) {
        // Ignore empty words
        continue
      }

      cuts.push({
        start: Math.max(convertTimeStringToSeconds(word.startTime) - minCut, 0),
        end: convertTimeStringToSeconds(word.endTime) + minCut,
      })
    }
  }

  return reduceRedundantCuts(cuts)
}

const intersectArrays = (a, b) => {
  const result = []
  let ai = 0
  const alength = a.length
  let ax
  let ay
  let bi = 0
  const blength = b.length
  let bx
  let by

  while (ai < alength && bi < blength) {
    ax = a[ai].start
    ay = a[ai].end
    bx = b[bi].start
    by = b[bi].end
    if (ay < bx) {
      // a ends before b
      ai += 1
    } else if (by < ax) {
      // b ends before a
      bi += 1
    } else {
      // a overlaps b
      result.push({ start: ax > bx ? ax : bx, end: ay < by ? ay : by })
      // the smaller range is considered processed
      if (ay < by) {
        ai += 1
      } else {
        bi += 1
      }
    }
  }
  return result
}

const intersectAll = (arrays) => {
  const result = arrays.slice(1).reduce((acc, current) => (
    intersectArrays(acc, current)
  ), arrays[0]).filter(({ start, end }) => end > start)
  return reduceRedundantCuts(result)
}

const invertInterval = (array, maxDuration) => {
  const latestEndTime = array[array.length - 1]?.end || 0

  return array
    .map(({ start: startTime }, index) => {
      const isFirst = index === 0
      const lastEndTime = isFirst ? 0 : array[index - 1].end

      return { start: lastEndTime, end: startTime }
    })
    .concat([{ start: latestEndTime, end: maxDuration }])
    .filter(({ start, end }) => end > start)
}

function mergeIntervals(arr1, arr2) {
  // Combine both arrays
  const intervals = [...arr1, ...arr2]

  // Sort intervals based on the start time; if start times are the same, sort by end time
  intervals.sort((a, b) => {
    if (a.start !== b.start) {
      return a.start - b.start
    }
    return a.end - b.end
  })

  // Array to hold merged intervals
  const merged = []

  // Iterate through sorted intervals and merge
  for (const interval of intervals) {
    // If merged is empty or if there is no overlap, add the interval to merged
    if (merged.length === 0 || merged[merged.length - 1].end < interval.start) {
      merged.push(interval)
    } else {
      // There is an overlap, so we merge the current interval with the last one in merged
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, interval.end)
    }
  }

  return merged
}

const eliminateSmallCuts = (intervals, maxTime, threshold = 0.2) => {
  const joinedIntervals = intervals.reduce((accumulated, interval, index) => {
    const difference = interval.end - interval.start
    if (difference < threshold) {
      return accumulated
    }

    const isLast = index === (intervals.length - 1)
    const nextTime = isLast ? maxTime : intervals[index + 1].start
    const differenceToNext = nextTime - interval.end
    if (differenceToNext < threshold) {
      return [
        ...accumulated,
        {
          start: interval.start,
          end: nextTime,
        },
      ]
    }

    return [
      ...accumulated,
      {
        start: interval.start,
        end: interval.end,
      },
    ]
  }, [])

  return reduceRedundantCuts(joinedIntervals)
}

function calculateClipIntervals(project, videoMetadata, additionalClipIntervals) {
  const fps = project.data.frameRate || VIDEO_FPS
  const exportedScenes = reduceRedundantCuts(project.crop.scenes.filter((scene) => scene.exported))
  const silenceClipIntervals = getSilenceClipIntervals(videoMetadata, {
    segments: (
      project.subtitle?.originalTranscription?.segments
      || project.subtitle.transcription.segments
    ),
    minCut: project.subtitle.minSilenceToCut,
    cut: project.subtitle.cutSilence,
  })
  const pureSilenceClipIntervals = invertInterval(getSilenceClipIntervals(videoMetadata, {
    segments: (
      project.subtitle?.originalTranscription?.segments
      || project.subtitle.transcription.segments
    ),
    minCut: project.subtitle.minSilenceToCut,
    cut: true,
  }), videoMetadata.durationInSeconds)
  const rawSubtitleClipIntervals = getSilenceClipIntervals(videoMetadata, {
    segments: project.subtitle.transcription.segments,
    minCut: project.subtitle.minSubtitleSilenceToCut,
    cut: project.subtitle.keepOnlySubtitles,
  })
  const subtitleClipIntervals = mergeIntervals(
    rawSubtitleClipIntervals,
    pureSilenceClipIntervals,
  )

  let finalStartFrame = 0
  let nextFinalStartFrame = 0
  let previousFinalEndFrame = 0
  return eliminateSmallCuts(intersectAll([
    additionalClipIntervals,
    exportedScenes,
    silenceClipIntervals,
    subtitleClipIntervals,
  ]), videoMetadata.durationInSeconds, project.subtitle.minSilenceToCut).map((clip) => {
    finalStartFrame = nextFinalStartFrame

    const startFrame = Math.round(fps * clip.start)
    const endFrame = Math.round(fps * clip.end)
    nextFinalStartFrame = previousFinalEndFrame + (endFrame - startFrame)
    previousFinalEndFrame = nextFinalStartFrame

    return {
      ...clip,
      startFrame,
      endFrame,
      finalStartFrame,
      finalEndFrame: nextFinalStartFrame,
    }
  })
}

function formatTime(timeInSeconds) {
  if (timeInSeconds < 0) {
    throw new Error('Non-negative timestamp expected')
  }

  let milliseconds = Math.round(timeInSeconds * 1000.0)

  const hours = Math.floor(milliseconds / 3_600_000)
  milliseconds -= hours * 3_600_000
  const hoursMarker = hours.toString().padStart(2, '0')

  const minutes = Math.floor(milliseconds / 60_000)
  milliseconds -= minutes * 60_000
  const minutesMarker = minutes.toString().padStart(2, '0')

  const seconds = Math.floor(milliseconds / 1_000)
  milliseconds -= seconds * 1_000
  const secondsMarker = seconds.toString().padStart(2, '0')

  return `${hoursMarker}:${minutesMarker}:${secondsMarker}.${milliseconds.toString().padStart(3, '0')}`
}

const getCropCenter = (scene, videoMetadata) => {
  if (scene.cropCenter) {
    return {
      x: (scene.cropCenter.x * videoMetadata.width) / 100,
      y: (scene.cropCenter.y * videoMetadata.height) / 100,
    }
  }

  const firstFace = scene.faces?.[0]
  if (firstFace) {
    return {
      x: firstFace?.x || (videoMetadata.width / 2),
      y: firstFace?.y || (videoMetadata.height / 2),
    }
  }

  return {
    x: (videoMetadata.width / 2),
    y: (videoMetadata.height / 2),
  }
}

const DEFAULT_EXPORT_DATA = {
  input: {
    media: { url: null },
  },
  data: {
    overlays: { watermark: null, front: [], back: [] },
    clipStart: null,
    clipEnd: null,
    frameRate: VIDEO_FPS,
    compressedEditableVideo: null,
    clipIntervals: [],
  },
  crop: {
    aspectRatio: 0.5625,
    scenes: [],
  },
  subtitle: {
    editByLine: true,
    originalTranscription: { segments: [] },
    transcription: { segments: [] },
    extraTimeTolerance: 0.75,
    animation: 'Fade In - Peek Ahead',
    cutSilence: true,
    keepOnlySubtitles: true,
    minSilenceToCut: 0.5,
    minSubtitleSilenceToCut: 0.5,
    lineStyle: {
      alignItems: 'flex-end', fontColor: '#ebe9f1', fontSize: 60, fontName: 'font-7V06JrBzrsey3fsqo9ZI7P2GSGhnWy', highlightCurrentWord: true, horizontalMarginPercentage: 20, verticalMarginPercentage: 30, shadowColor: '#050505', shadowSize: 40, justifyContent: 'center', textTransform: 'uppercase', gap: 0.3, lineHeight: 1, enableBackground: false, padding: 0.5, borderRadius: 20, backgroundColor: 'black', paddingY: 0.5, paddingX: 0.3,
    },
    highlightStyle: { fontColor: '#ff0000' },
    highlightBoxStyle: {
      enabled: false, backgroundColor: '#4023d1', borderRadius: 20, paddingX: 0.3, paddingY: 0.05,
    },
  },
}

export const getPresetedExportData = (clip, metadata, subtitlePresets) => {
  metadata.durationInSeconds = metadata.durationInSeconds || metadata.duration;
  const preset = subtitlePresets.find((p) => p.id === clip.data.preset)

  const duration = metadata.durationInSeconds || metadata.duration || clip.duration

  let scenes = (clip.data.scenes || []).map((scene) => {
    const cropCenter = getCropCenter(scene, metadata)
    return {
      start: scene.start,
      end: scene.end,
      exported: true,
      cropCenter,
    }
  })

  // Fallback: full-duration center-crop scene when no scenes exist
  if (scenes.length === 0) {
    scenes = [{
      start: 0,
      end: duration,
      exported: true,
      cropCenter: {
        x: metadata.width / 2,
        y: metadata.height / 2,
      },
    }]
  }

  const lines = convertWordsToLines(clip.data.words || [], {
    maxSeconds: 20,
    maxWords: 6,
    maxCharacters: 25,
  }).map((line) => ({
    ...line,
    startTime: formatTime(line.start),
    endTime: formatTime(line.end),
    words: line.words.map((word) => ({
      ...word,
      startTime: formatTime(word.start),
      endTime: formatTime(word.end),
    })),
  }))

  const exportData = {
    ...DEFAULT_EXPORT_DATA,
    input: {
      media: {
        url: clip.clipped_video_url,
      },
    },
    crop: {
      ...DEFAULT_EXPORT_DATA.crop,
      scenes,
    },
    data: {
      ...DEFAULT_EXPORT_DATA.data,
      clipStart: 0,
      clipEnd: duration,
      compressedEditableVideo: clip.compressed_video_url,
      clipIntervals: [
        {
          start: 0,
          end: duration,
          startFrame: 0,
          endFrame: Math.floor(duration * VIDEO_FPS),
          finalStartFrame: 0,
          finalEndFrame: Math.floor(duration * VIDEO_FPS),
        },
      ],
    },
    subtitle: {
      ...DEFAULT_EXPORT_DATA.subtitle,
      originalTranscription: {
        segments: lines,
      },
      transcription: {
        segments: lines,
      },
    },
  }

  if (preset) {
    preset.preset.forEach((change) => {
      if (change.type === 'set-value') {
        lodash.set(exportData, change.attribute.replace('project.', ''), change.value)
      }
    })
  }

  const clipIntervals = calculateClipIntervals(exportData, metadata, [{
    start: 0,
    end: duration,
  }])
  exportData.data.clipIntervals = clipIntervals

  return exportData
}
